import React, { useState, memo } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { Package, Lock, Unlock, Eye, User, CheckSquare, Square, Ban, ChevronDown, MapPin, Info, AlertTriangle, Truck, CalendarDays } from 'lucide-react';
import { PListDto, PListLineDto } from '../../../../types/picking';
import { pickingService } from '../../../../services/pickingService';
import { OrderListBadge, splitOrderNoERPs } from './OrderListBadge';
import { DueStatus, getDueStatus, formatDeliveryDate } from './pickingFilters';

// Leveringsdato-farger: forfalt rød, i dag gul, senere nøytral (alle klassene har dark-overrides i index.css)
const DUE_CHIP_CLASS: Record<DueStatus, string> = {
  overdue: 'bg-red-100 text-red-700',
  today: 'bg-amber-100 text-amber-700',
  future: 'bg-gray-100 text-gray-700'
};
const DUE_ICON_CLASS: Record<DueStatus, string> = {
  overdue: 'text-red-600',
  today: 'text-amber-700',
  future: 'text-gray-400'
};

interface PickingCardActionsProps {
  list: PListDto;
  isOwned: boolean;
  onStartPicking: (list: PListDto) => Promise<void>;
  onLockList: (pListId: number) => Promise<void>;
  onUnlockList: (pListId: number) => Promise<void>;
  onPreviewList: (pListId: number) => void;
  // Anti-cherry-picking (styrt av 'picking.hideViewButton'): skjuler «Vis», og for plukk operatøren
  // ikke eier også «Start» og «Ta plukk» (TW 36397707) — da er «Neste plukk» eneste vei inn.
  // Egne plukk («Mine plukk») beholder Start + Lås opp.
  hideViewButton?: boolean;
}

// Separate component for actions to isolate loading states
export const PickingCardActions: React.FC<PickingCardActionsProps> = memo(({
  list,
  isOwned,
  onStartPicking,
  onLockList,
  onUnlockList,
  onPreviewList,
  hideViewButton = false
}) => {
  const { t } = useTranslation();
  const [startingList, setStartingList] = useState(false);
  const [lockingList, setLockingList] = useState(false);

  const handleStartPicking = async () => {
    try {
      setStartingList(true);
      await onStartPicking(list);
    } catch (err) {
      console.error('Error starting picking:', err);
    } finally {
      setStartingList(false);
    }
  };

  const handleLockList = async () => {
    try {
      setLockingList(true);
      await onLockList(list.pListID);
    } catch (err) {
      console.error('Error locking list:', err);
    } finally {
      setLockingList(false);
    }
  };

  const handleUnlockList = async () => {
    try {
      setLockingList(true);
      await onUnlockList(list.pListID);
    } catch (err) {
      console.error('Error unlocking list:', err);
    } finally {
      setLockingList(false);
    }
  };

  // Ikke-eid plukk med anti-cherry-picking på: ingen handlinger (TW 36397707)
  if (hideViewButton && !isOwned) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <button
        onClick={handleStartPicking}
        disabled={startingList}
        className="flex items-center gap-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 transition-colors disabled:opacity-50 min-w-[60px] justify-center"
      >
        {startingList ? (
          <>
            <div className="w-2.5 h-2.5 animate-spin border border-white border-t-transparent rounded-full" />
          </>
        ) : (
          <>
            <Package size={12} />
            <span>{t('warehouse.modules.picking.actions.start')}</span>
          </>
        )}
      </button>
      
      {isOwned ? (
        <button
          onClick={handleUnlockList}
          disabled={lockingList}
          className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded text-xs hover:bg-red-200 transition-colors disabled:opacity-50 min-w-[60px] justify-center"
        >
          {lockingList ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-red-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Unlock size={12} />
              <span>{t('warehouse.modules.picking.actions.unlockList')}</span>
            </>
          )}
        </button>
      ) : (
        <button
          onClick={handleLockList}
          disabled={lockingList}
          className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs hover:bg-green-200 transition-colors disabled:opacity-50 min-w-[70px] justify-center"
        >
          {lockingList ? (
            <>
              <div className="w-2.5 h-2.5 animate-spin border border-green-500 border-t-transparent rounded-full" />
            </>
          ) : (
            <>
              <Lock size={12} />
              <span>{t('warehouse.modules.picking.actions.lockList')}</span>
            </>
          )}
        </button>
      )}

      {!hideViewButton && (
        <button
          onClick={() => onPreviewList(list.pListID)}
          className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-200 transition-colors min-w-[50px] justify-center"
        >
          <Eye size={12} />
          <span>{t('warehouse.modules.picking.actions.preview')}</span>
        </button>
      )}
    </div>
  );
});

PickingCardActions.displayName = 'PickingCardActions';

interface PickingCardProps {
  list: PListDto;
  isOwned: boolean;
  onStartPicking: (list: PListDto) => Promise<void>;
  onLockList: (pListId: number) => Promise<void>;
  onUnlockList: (pListId: number) => Promise<void>;
  onPreviewList: (pListId: number) => void;
  // Batch selection mode props
  batchSelectMode?: boolean;
  isSelectedForBatch?: boolean;
  isBatchSelectable?: boolean;
  batchSelectDisabledReason?: string;
  onToggleBatchSelection?: (pListId: number) => void;
  // Pre-fetched lines from batch mode — avoids re-fetch on accordion expand
  cachedLines?: PListLineDto[];
  // K7: Toggle visibility of customer name. Controlled by `picking.showCustomerName`.
  showCustomerName?: boolean;
  // Anti-cherry-picking: skjul «Vis»-knappen. Controlled by `picking.hideViewButton`.
  hideViewButton?: boolean;
}

// Main card component with memoization
export const PickingCard: React.FC<PickingCardProps> = memo(({
  list,
  isOwned,
  onStartPicking,
  onLockList,
  onUnlockList,
  onPreviewList,
  batchSelectMode = false,
  isSelectedForBatch = false,
  isBatchSelectable = true,
  batchSelectDisabledReason,
  onToggleBatchSelection,
  cachedLines,
  showCustomerName = true,
  hideViewButton = false
}) => {
  const { t } = useTranslation();

  // K4/K7 dedup: PList description-feltene inneholder ofte orderNoERPs og/eller
  // kundenavn fra før (legacy-data). Skjul de nye elementene som er duplikat så
  // kortet ikke viser samme info to ganger.
  // Sammenlignes som mengde: rekkefølgen i Description2 (regelmotor) og OrderNoERPs (SQL, OrderNo)
  // trenger ikke være lik (TW 36416444).
  const orderNos = splitOrderNoERPs(list.orderNoERPs);
  const normalizeOrderSet = (value: string) => splitOrderNoERPs(value.toLowerCase()).sort().join('\n');
  const description2IsRedundant = orderNos.length > 0 && !!list.description2 &&
    normalizeOrderSet(list.description2.trim().replace(/^order\s+/i, '')) === normalizeOrderSet(list.orderNoERPs ?? '');
  const effectiveDescription2 = description2IsRedundant ? undefined : list.description2;

  const customerInDescription = (() => {
    const name = list.customerNames?.toLowerCase().trim();
    if (!name) return false;
    const haystack = `${list.description1 ?? ''} ${list.description2 ?? ''}`.toLowerCase();
    return haystack.includes(name);
  })();
  const showCustomerNameRow = showCustomerName && !!list.customerNames && !customerInDescription;

  // Leveringsdato (PList.DeliveryDate — samme felt som «I dag og forfalte»-filteret).
  // Desktop: fargekodet brikke. Mobil: fargekodet ikon som viser datoen ved trykk (lite plass).
  const dueStatus = getDueStatus(list.deliveryDate);
  const deliveryDateText = dueStatus && list.deliveryDate ? formatDeliveryDate(list.deliveryDate) : '';
  const dueStatusText = dueStatus === 'overdue'
    ? t('warehouse.modules.picking.card.dueOverdue', {}, 'forfalt')
    : dueStatus === 'today'
      ? t('warehouse.modules.picking.card.dueToday', {}, 'i dag')
      : '';
  const [showDueDateMobile, setShowDueDateMobile] = useState(false);
  const handleDueDateToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Ikke velg kortet i samleplukk-modus
    setShowDueDateMobile(prev => !prev);
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 900) return 'bg-red-500';
    if (priority >= 500) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 10: return 'bg-green-500'; // Godkjent
      case 20: return 'bg-blue-500';  // Under arbeid
      case 30: return 'bg-yellow-500'; // Behandlet, ikke komplett
      case 40: return 'bg-green-600';  // Behandlet, komplett
      case 45: return 'bg-purple-500'; // Til pakking
      default: return 'bg-gray-400';   // Default/Ubehandlet
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return t('warehouse.modules.picking.status.unprocessed');
      case 10: return t('warehouse.modules.picking.status.approved');
      case 20: return t('warehouse.modules.picking.status.inProgress');
      case 30: return t('warehouse.modules.picking.status.processedIncomplete');
      case 40: return t('warehouse.modules.picking.status.processedComplete');
      case 45: return t('warehouse.modules.picking.status.toPacking');
      case 50: return t('warehouse.modules.picking.status.readyForExport');
      case 60: return t('warehouse.modules.picking.status.exported');
      default: return t('warehouse.modules.picking.status.unknown', { '0': status.toString() });
    }
  };

  // Accordion state for line inspection in batch mode
  const [isExpanded, setIsExpanded] = useState(false);
  const [inspectLines, setInspectLines] = useState<PListLineDto[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  const handleBatchToggle = () => {
    if (batchSelectMode && isBatchSelectable && onToggleBatchSelection) {
      onToggleBatchSelection(list.pListID);
    }
  };

  const handleExpandToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger batch selection
    if (isExpanded) {
      setIsExpanded(false);
      return;
    }
    // Use cached lines from batch pre-fetch if available, otherwise lazy-load
    if (inspectLines.length === 0) {
      if (cachedLines && cachedLines.length > 0) {
        setInspectLines(cachedLines);
      } else {
        setLoadingLines(true);
        try {
          const details = await pickingService.getPickingDetails(list.pListID);
          setInspectLines(details.lines || []);
        } catch {
          // Silently fail — user can try again
        } finally {
          setLoadingLines(false);
        }
      }
    }
    setIsExpanded(true);
  };

  // Card style varies based on mode
  const getCardClassName = () => {
    // min-w-0: kortet ligger i en CSS-grid (ModernPickingList) der kolonnen ellers blir minst like bred
    // som den lengste nowrap-teksten (f.eks. flere kundenavn) og presser alle kortene ut av skjermen (TW 36416444).
    const base = 'rounded-lg shadow-sm border-2 p-3 transition-all duration-200 min-w-0';
    if (batchSelectMode) {
      if (isSelectedForBatch) {
        return `${base} border-[#F58220] bg-orange-50 ring-2 ring-orange-200 cursor-pointer`;
      }
      if (!isBatchSelectable) {
        return `${base} border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed`;
      }
      return `${base} border-gray-200 bg-white hover:border-orange-300 cursor-pointer`;
    }
    if (isOwned) {
      return `${base} border-green-200 bg-green-50 hover:shadow-md hover:border-green-300`;
    }
    return `${base} border-gray-200 bg-white hover:shadow-md hover:border-gray-300`;
  };

  return (
    <div
      className={getCardClassName()}
      onClick={batchSelectMode ? handleBatchToggle : undefined}
      role={batchSelectMode ? 'checkbox' : undefined}
      aria-checked={batchSelectMode ? isSelectedForBatch : undefined}
    >
      {/* Responsive Layout with flex wrap */}
      {/* sm:flex-nowrap: fra sm skal tittel/beskrivelse krympe (min-w-0) i stedet for at knappekolonnen brytes ned */}
      <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
        {/* Shortage warning - fixed-width slot at far left so rows stay aligned whether or not the icon shows */}
        <div className="flex items-center justify-center w-4 flex-shrink-0 self-center">
          {list.hasShortageLines && (
            <span
              className="inline-flex"
              title={t('warehouse.modules.picking.card.shortageWarning', {}, 'Inneholder linjer uten saldo (manko) – disse er ikke plukkbare')}
            >
              <AlertTriangle size={15} className="text-amber-500" aria-label={t('warehouse.modules.picking.card.shortageWarning', {}, 'Inneholder linjer uten saldo (manko) – disse er ikke plukkbare')} />
            </span>
          )}
        </div>
        {/* Left side - Checkbox (batch mode) or Status dot + ID. min-w-0 så en lang tittel kortes ned
            i stedet for å skyve knappene ut av kortet (TW 36416444). */}
        <div className="flex items-center gap-3 min-w-0">
          {batchSelectMode ? (
            <div className="flex items-center justify-center w-7 h-7 flex-shrink-0">
              {!isBatchSelectable ? (
                <Ban size={18} className="text-gray-400" />
              ) : isSelectedForBatch ? (
                <CheckSquare size={20} className="text-[#F58220]" />
              ) : (
                <Square size={20} className="text-gray-400" />
              )}
            </div>
          ) : (
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusColor(list.pListStatus)}`} />
          )}
          {/* Flere ordrer: første ordrenummer + «+N ordrer» (trykk viser alle). title-attributtet
              brukes som selektor av Compass-Testers Playwright-oppskrift — ikke endre det. */}
          <h3 className="text-sm font-semibold text-gray-900 whitespace-nowrap truncate min-w-0" title={t('warehouse.modules.picking.card.pickNumber', { '0': list.pListID })}>
            {orderNos.length > 0 ? orderNos[0] : `#${list.pListID}`}
          </h3>
          <OrderListBadge orderNoERPs={orderNos} companyNo={list.companyNo} pListId={list.pListID} className="-ml-1.5" />
          {/* Ariens: landkode + EXPORT for utenlandsordrer (leveringsland ≠ firmaland) */}
          {list.isExport && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 whitespace-nowrap flex-shrink-0"
              title={t('warehouse.modules.picking.card.exportTitle', {}, 'Eksportordre – leveringsland avviker fra firmaland')}
            >
              {list.delCountryCodes ? `${list.delCountryCodes} · EXPORT` : 'EXPORT'}
            </span>
          )}
          {/* Norlux (punkt 1): NVIT — innenlandsordre i transitt via Sverige (krever tolldokumenter) */}
          {list.isNvit && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 whitespace-nowrap flex-shrink-0"
              title={t('warehouse.modules.picking.card.nvitTitle', {}, 'NVIT – norske varer i transitt via Sverige (tolldokumenter kreves)')}
            >
              NVIT
            </span>
          )}
        </div>

        {/* Center - Description */}
        <div className="flex-1 px-3 min-w-0">
          <div className="text-xs text-gray-600 truncate">
            <span className="font-bold">{list.description1 || '-'}</span>
            {effectiveDescription2 && (
              <span className="text-gray-500 ml-1">| {effectiveDescription2}</span>
            )}
          </div>
          {/* K7: Customer name(s) — recipient (DelCustomerName) preferred, comma-separated for multi-order */}
          {showCustomerNameRow && (
            <div className="text-[11px] text-gray-500 truncate mt-0.5">
              <User size={10} className="inline mr-1 text-gray-400" />
              {list.customerNames}
            </div>
          )}
          {/* Leveringsmåte (TW 36403718): alltid på, egen rad og litt større tekst slik at
              plukkeren ser «KUNDE ER HER NÅ» før plukket tas. Tom når ordren mangler leveringsmåte. */}
          {list.deliveryMethodDescription && (
            <div className="text-sm font-semibold text-gray-800 truncate mt-0.5">
              <Truck size={13} className="inline mr-1 text-gray-500 -mt-0.5" />
              {list.deliveryMethodDescription}
            </div>
          )}
          {/* Show disabled reason in batch mode */}
          {batchSelectMode && !isBatchSelectable && batchSelectDisabledReason && (
            <div className="text-[10px] text-red-500 mt-0.5">{batchSelectDisabledReason}</div>
          )}
        </div>

        {/* Right column (desktop) - Actions on top, lines count + priority + expand button below.
            flex-shrink-0: knappene skyves aldri ut av kortet (TW 36416444). */}
        <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
          <div className="flex items-center gap-2">
            {/* Leveringsdato foran knappene, fargekodet etter forfall */}
            {dueStatus && (
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${DUE_CHIP_CLASS[dueStatus]}`}>
                {t('warehouse.modules.picking.card.deliveryDate', { '0': deliveryDateText }, `Lev. dato ${deliveryDateText}`)}
              </span>
            )}
            {/* Actions - hidden in batch mode (mobile has its own row below) */}
            {!batchSelectMode && (
              <PickingCardActions
                list={list}
                isOwned={isOwned}
                onStartPicking={onStartPicking}
                onLockList={onLockList}
                onUnlockList={onUnlockList}
                onPreviewList={onPreviewList}
                hideViewButton={hideViewButton}
              />
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{list.numOfOrderLines || 0}</span>
            <span className="text-gray-500">P:{list.priority}</span>
            {batchSelectMode && (
              <button
                onClick={handleExpandToggle}
                className="ml-2 p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                title={t('warehouse.modules.picking.card.showPickingLines')}
              >
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Mobile second line - Actions (normal mode only) + Lines count + Priority */}
        <div className="flex sm:hidden items-center justify-between w-full">
          {!batchSelectMode && (
            <PickingCardActions
              list={list}
              isOwned={isOwned}
              onStartPicking={onStartPicking}
              onLockList={onLockList}
              onUnlockList={onUnlockList}
              onPreviewList={onPreviewList}
              hideViewButton={hideViewButton}
            />
          )}
          <div className={`flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap ${batchSelectMode ? 'ml-auto' : ''}`}>
            {/* Leveringsdato som fargekodet ikon — trykk viser datoen under raden.
                44px trykkflate med negativ marg så raden ikke blir høyere. */}
            {dueStatus && (
              <button
                type="button"
                onClick={handleDueDateToggle}
                aria-expanded={showDueDateMobile}
                aria-label={t('warehouse.modules.picking.card.deliveryDateLong', { '0': deliveryDateText }, `Leveringsdato ${deliveryDateText}`)}
                className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -my-2.5 -ml-2"
              >
                <CalendarDays size={16} className={DUE_ICON_CLASS[dueStatus]} />
              </button>
            )}
            <Package size={12} className="text-gray-400" />
            <span className="font-medium">{list.numOfOrderLines || 0}</span>
            <span className="text-gray-500">P:{list.priority}</span>
            {batchSelectMode && (
              <button
                onClick={handleExpandToggle}
                className="ml-1 p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                title={t('warehouse.modules.picking.card.showPickingLines')}
              >
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobil: leveringsdato vist etter trykk på kalender-ikonet */}
      {dueStatus && showDueDateMobile && (
        <div className="sm:hidden mt-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${DUE_CHIP_CLASS[dueStatus]}`}>
            <CalendarDays size={12} />
            {t('warehouse.modules.picking.card.deliveryDateLong', { '0': deliveryDateText }, `Leveringsdato ${deliveryDateText}`)}
            {dueStatusText && <span className="font-normal">· {dueStatusText}</span>}
          </span>
        </div>
      )}

      {/* Accordion: Line inspection (batch mode only) */}
      {batchSelectMode && isExpanded && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          {loadingLines ? (
            <div className="flex items-center gap-2 py-2 text-xs text-gray-500">
              <div className="w-3 h-3 animate-spin border border-gray-400 border-t-transparent rounded-full" />
              {t('warehouse.modules.picking.card.loadingPickingLines')}
            </div>
          ) : inspectLines.length === 0 ? (
            <div className="text-xs text-gray-400 py-1">{t('warehouse.modules.picking.card.noPickingLines')}</div>
          ) : (
            <div className="space-y-1">
              {/* Header row */}
              <div className="hidden sm:grid sm:grid-cols-[1fr_2fr_1fr_auto] gap-2 text-[10px] font-semibold text-gray-400 uppercase px-1">
                <span>{t('warehouse.general.item')}</span>
                <span>{t('common.fields.description')}</span>
                <span>{t('warehouse.general.bin')}</span>
                <span className="text-right">{t('common.fields.quantity')}</span>
              </div>
              {inspectLines.map(line => (
                <div
                  key={line.pListLineNo}
                  className="flex flex-col sm:grid sm:grid-cols-[1fr_2fr_1fr_auto] gap-0.5 sm:gap-2 text-xs px-1 py-1 rounded hover:bg-gray-50"
                >
                  {/* Mobile: compact stacked layout */}
                  <div className="sm:hidden flex items-center justify-between">
                    <span className="font-mono text-gray-700 font-medium text-[11px]">{line.itemNo || '-'}</span>
                    <div className="flex items-center gap-1 text-gray-500">
                      <MapPin size={10} />
                      <span className="font-mono text-[11px]">{line.binCode || '-'}</span>
                      <span className="ml-2 font-semibold text-gray-800">{line.qty}</span>
                    </div>
                  </div>
                  <div className="sm:hidden text-[11px] text-gray-500 truncate">{line.itemDescription || '-'}</div>

                  {/* Desktop: grid layout */}
                  <span className="hidden sm:block font-mono text-gray-700 font-medium truncate">{line.itemNo || '-'}</span>
                  <span className="hidden sm:block text-gray-600 truncate">{line.itemDescription || '-'}</span>
                  <span className="hidden sm:flex items-center gap-1 text-gray-500 font-mono">
                    <MapPin size={10} className="flex-shrink-0" />
                    {line.binCode || '-'}
                  </span>
                  <span className="hidden sm:block text-right font-semibold text-gray-800">{line.qty}</span>
                </div>
              ))}
              {/* Summary */}
              {/* Summary */}
              {(() => {
                const totalQty = inspectLines.reduce((sum, l) => sum + l.qty, 0);
                return (
                  <div className="flex items-center gap-3 pt-1 mt-1 border-t border-gray-100 text-[10px] text-gray-400 px-1">
                    <span>{inspectLines.length === 1 ? t('warehouse.modules.picking.card.lineCountSingular', { '0': inspectLines.length }) : t('warehouse.modules.picking.card.lineCountPlural', { '0': inspectLines.length })}</span>
                    <span className="font-semibold text-gray-500">{t('warehouse.modules.picking.card.totalUnits', { '0': totalQty % 1 === 0 ? totalQty : totalQty.toFixed(1) })}</span>
                    {list.weight ? <span>{t('warehouse.modules.picking.card.weightKg', { '0': list.weight.toFixed(1) })}</span> : null}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Only re-render if essential props change, not loading states
  return (
    prevProps.list.pListID === nextProps.list.pListID &&
    prevProps.list.pListStatus === nextProps.list.pListStatus &&
    prevProps.list.lockedBy === nextProps.list.lockedBy &&
    prevProps.isOwned === nextProps.isOwned &&
    prevProps.list.priority === nextProps.list.priority &&
    prevProps.list.description1 === nextProps.list.description1 &&
    prevProps.list.description2 === nextProps.list.description2 &&
    prevProps.list.orderNoERPs === nextProps.list.orderNoERPs &&
    prevProps.list.customerNames === nextProps.list.customerNames &&
    prevProps.list.numOfOrderLines === nextProps.list.numOfOrderLines &&
    prevProps.list.hasShortageLines === nextProps.list.hasShortageLines &&
    prevProps.list.isExport === nextProps.list.isExport &&
    prevProps.list.isNvit === nextProps.list.isNvit &&
    prevProps.list.delCountryCodes === nextProps.list.delCountryCodes &&
    prevProps.list.deliveryDate === nextProps.list.deliveryDate &&
    prevProps.batchSelectMode === nextProps.batchSelectMode &&
    prevProps.isSelectedForBatch === nextProps.isSelectedForBatch &&
    prevProps.isBatchSelectable === nextProps.isBatchSelectable &&
    // hideViewButton lastes asynkront (settings-hook) og flipper ETTER første render —
    // uten denne sammenligningen sluker memo-en endringen og «Vis»-knappen blir stående
    prevProps.hideViewButton === nextProps.hideViewButton &&
    (prevProps.cachedLines?.length || 0) === (nextProps.cachedLines?.length || 0)
  );
});

PickingCard.displayName = 'PickingCard';